import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import type { JsonTimesheetAddressModel } from '../../../models/complex/timesheet-address.model';
import { JsonTimesheetEntryModel } from '../../../models/complex/timesheet-entry.model';
import { JsonTimesheetManualEntryModel } from '../../../models/complex/timesheet-manual-entry.model';
import { JsonTimesheetNotificationModel } from '../../../models/low-level/timesheet-notification.model';
import { JsonTimesheetModel } from '../../../models/low-level/timesheet.model';
import { addressesById, AddressState, toJsonTimesheetAddress } from '../../address';
import { TimesheetManualEntryState, TimesheetState, TimesheetWorkEntryState } from '../timesheet-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Re-nests the flat timesheet slice into the legacy `JsonTimesheetModel` shape.
 *
 * This is a bridge with a known end date: the confirmation, header, side-menu and
 * travel-status-update code paths still read the timesheet as a `TimesheetModel` through the
 * facade, and `TimesheetModelFromJson` expects work entries grouped under their notification and
 * manual entries in their own array. It dies together with the facade.
 *
 * Two shape details the legacy models require:
 * - a notification `distance` must always be a `JsonTextModel` object (`TimesheetNotificationModelFromJson`
 *   constructs one with no null guard), while an entry `distance` must be omitted when zero
 *   (`TimesheetEntryModelFromJson` guards it, and an empty one would emit `Distance: ''`);
 * - the `startDate`/`startTime` and `endDate`/`endTime` pairs both carry the entry's single
 *   instant, which is what the rehydration reads back.
 *
 * A third detail follows from the address slice migration: an entry stores only its address's id,
 * so this bridge is the one place that resolves it back into the legacy JSON address shape — the
 * cast below is what makes a wrong resolution here invisible to the compiler.
 */

/**
 * Grouping key for the notification an entry was recorded against.
 *
 * @param notificationId the entry's identifier
 * @returns a stable string key for the identifier
 */
const notificationKey = (notificationId: NotificationIdState): string =>
    `${notificationId.frontId}:${notificationId.qmNum}:${notificationId.maNum}`;

/**
 * Wraps a numeric distance in the `JsonTextModel` shape a notification requires.
 *
 * @param distance the distance in the slice's numeric form
 * @returns the wrapped distance, empty-valued when there is none
 */
const notificationDistance = (distance: number): JsonTimesheetNotificationModel['distance'] =>
    ({
        name: 'Distance',
        value: distance > 0 ? distance.toString() : '',
        options: {},
    }) as unknown as JsonTimesheetNotificationModel['distance'];

/**
 * Wraps a numeric distance for an entry, omitted entirely when there is none.
 *
 * @param distance the distance in the slice's numeric form
 * @returns the wrapped distance, or undefined so no `Distance` field is emitted
 */
const entryDistance = (distance: number): JsonTimesheetEntryModel['distance'] | undefined =>
    distance > 0
        ? ({
              name: 'Distance',
              value: distance.toString(),
              options: {},
          } as unknown as JsonTimesheetEntryModel['distance'])
        : undefined;

// The `Json*` entry types declare `address`, `distance` and `travelType` as required, while the
// rehydration guards all three for absence — and their `address` is the legacy model's own JSON
// shape, not the slice's (nor the slice's stored id). So these builders describe what they really
// produce and the single assertion in `toJsonTimesheetModel` is the one place that claims the
// legacy shape.

/** What {@link toJsonWorkEntry} produces: a legacy work entry with the guarded fields optional. */
type WorkEntryJsonLike = Omit<JsonTimesheetEntryModel, 'address' | 'distance'> & {
    address?: JsonTimesheetAddressModel;
    distance?: JsonTimesheetEntryModel['distance'];
};

/** What {@link toJsonNotifications} produces: a legacy notification holding the relaxed entries. */
type NotificationJsonLike = Omit<JsonTimesheetNotificationModel, 'timesheetEntries'> & {
    timesheetEntries: WorkEntryJsonLike[];
};

/** What {@link toJsonManualEntry} produces, with the same relaxations. */
type ManualEntryJsonLike = Omit<JsonTimesheetManualEntryModel, 'address' | 'travelType'> & {
    address?: JsonTimesheetAddressModel;
    travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
};

/**
 * Resolves an entry's stored address id back into the legacy JSON address shape. A dangling id
 * (its address deleted after the entry was recorded) resolves to no address, same as the slice's
 * own `undefined`.
 *
 * @param addressId the entry's stored address id, absent when it has none
 * @param addresses the address collection indexed by id
 * @param userCountry the country every timesheet address carries
 * @returns the legacy JSON address, or undefined when the id is absent or unknown
 */
function resolveJsonAddress(
    addressId: string | null,
    addresses: ReadonlyMap<string, AddressState>,
    userCountry: string,
): JsonTimesheetAddressModel | undefined {
    const address = addressId ? addresses.get(addressId) : undefined;
    return address ? toJsonTimesheetAddress(address, userCountry) : undefined;
}

const toJsonWorkEntry = (
    entry: TimesheetWorkEntryState,
    addresses: ReadonlyMap<string, AddressState>,
    userCountry: string,
): WorkEntryJsonLike => ({
    notificationId: entry.notificationId,
    startDate: entry.start,
    startTime: entry.start,
    endDate: entry.end,
    endTime: entry.end,
    workCode: entry.workCode,
    activityType: entry.activityType,
    address: resolveJsonAddress(entry.address, addresses, userCountry),
    addressInOutModel: entry.addressInOutModel,
    distance: entryDistance(entry.distance),
    meansOfTransport: entry.meansOfTransport,
    isTravelBillable: entry.isTravelBillable,
    afterHourCallOut: entry.afterHourCallOut,
});

const toJsonManualEntry = (
    entry: TimesheetManualEntryState,
    addresses: ReadonlyMap<string, AddressState>,
    userCountry: string,
): ManualEntryJsonLike => ({
    startDateTime: entry.start,
    endDateTime: entry.end,
    // The slice does not store bounds — they are derived from the neighbouring rows for
    // display. Handing back the entry's own instants keeps the legacy model from clamping it.
    minStart: entry.start,
    maxEnd: entry.end,
    address: resolveJsonAddress(entry.address, addresses, userCountry),
    addressInOutModel: entry.addressInOutModel,
    taskType: entry.taskType,
    isTravelBillable: entry.isTravelBillable,
    notificationId: entry.notificationId,
    travelType: entry.travelType,
});

/**
 * Re-groups the flat work entries under the notifications they were recorded against, in
 * first-seen (so chronological) order.
 *
 * The slice keeps no notification records — customer name and the confirmation-submitted flag ride
 * along on the entries — so the legacy notification is reassembled from its first entry. Its
 * `distance` is the first positive entry distance of the group: the confirmation attaches the
 * distance it reported to the group's first TRTO entry, which is where legacy read it back from
 * too. A notification whose entries are all gone therefore no longer appears, which is what the
 * legacy consumers want anyway — they only ever ask about notifications that have entries.
 *
 * @param workEntries the timesheet's work entries, in slice order
 * @param addresses the address collection indexed by id
 * @param userCountry the country every timesheet address carries
 * @returns the legacy notification objects, each with its entries
 */
function toJsonNotifications(
    workEntries: TimesheetWorkEntryState[],
    addresses: ReadonlyMap<string, AddressState>,
    userCountry: string,
): NotificationJsonLike[] {
    const byNotification = new Map<string, TimesheetWorkEntryState[]>();
    for (const entry of workEntries) {
        const key = notificationKey(entry.notificationId);
        const group = byNotification.get(key);
        if (group) {
            group.push(entry);
        } else {
            byNotification.set(key, [entry]);
        }
    }

    return [...byNotification.values()].map((group) => ({
        notificationId: group[0].notificationId,
        customerName: group[0].customerName,
        distance: notificationDistance(group.find((entry) => entry.distance > 0)?.distance ?? 0),
        isConfirmationSubmitted: group[0].isConfirmationSubmitted,
        expenses: [],
        timesheetEntries: group.map((entry) => toJsonWorkEntry(entry, addresses, userCountry)),
    }));
}

/**
 * @param timesheet the flat timesheet slice
 * @param addresses the address collection a stored address id is resolved against
 * @param userCountry the country every timesheet address carries, read from user master
 * @returns the timesheet in the legacy nested JSON shape
 */
export function toJsonTimesheetModel(
    timesheet: TimesheetState,
    addresses: readonly AddressState[],
    userCountry: string,
): JsonTimesheetModel {
    const workEntries = timesheet.entries.filter((entry): entry is TimesheetWorkEntryState => entry.kind === 'work');
    const manualEntries = timesheet.entries.filter(
        (entry): entry is TimesheetManualEntryState => entry.kind === 'manual',
    );
    const addressesLookup = addressesById(addresses);

    return {
        timesheetId: timesheet.timesheetId,
        startOfWork: timesheet.startOfWork,
        endOfWork: timesheet.endOfWork,
        isSubmitted: timesheet.isSubmitted,
        attendanceType: timesheet.attendanceType,
        startAddressValue: timesheet.startAddressValue,
        endAddressValue: timesheet.endAddressValue,
        saveTimestamp: timesheet.saveTimestamp,
        isRemoteTimesheet: timesheet.isRemoteTimesheet,
        additionalExpenses: [],
        notifications: toJsonNotifications(workEntries, addressesLookup, userCountry),
        manualTimesheetEntries: manualEntries.map((entry) => toJsonManualEntry(entry, addressesLookup, userCountry)),
    } as unknown as JsonTimesheetModel;
}
